import { parseDose } from '@wodi/domain';
import type { DraftLineInput, FormularyOptionView, FrequencyView } from '@wodi/shared';

// The prescriber's draft lines as typed, and how they become API lines. Drafts live on
// the device only (plan P8); nothing is sent until "Sign prescription".

export interface DraftState {
  readonly key: string;
  readonly option: FormularyOptionView;
  readonly dose: string;
  readonly doseUnit: string;
  readonly routeCode: string;
  readonly frequencyCode: string;
  readonly maxDosesPerDay: string;
  readonly durationValue: string;
  readonly durationUnit: 'days' | 'weeks';
  /** Empty: dispense the calculated course. */
  readonly quantity: string;
  readonly instructions: string;
  readonly substitutionAllowed: boolean;
  readonly giveInClinic: boolean;
}

export function newDraft(key: string, option: FormularyOptionView): DraftState {
  const [onlyRoute] = option.routes;
  return {
    key,
    option,
    dose: '',
    doseUnit: option.doseUnit.code,
    routeCode:
      option.defaultRouteCode ?? (option.routes.length === 1 ? (onlyRoute?.code ?? '') : ''),
    frequencyCode: '',
    maxDosesPerDay: '',
    durationValue: '',
    durationUnit: 'days',
    quantity: '',
    instructions: '',
    substitutionAllowed: true,
    giveInClinic: false,
  };
}

export type DraftProblem = 'dose' | 'route' | 'frequency' | 'max_doses' | 'duration' | 'quantity';

const WHOLE = /^\d{1,6}$/;

/** What is missing or wrong on a line, in field order; empty when it can be checked. */
export function draftProblems(
  draft: DraftState,
  frequencies: readonly FrequencyView[],
): DraftProblem[] {
  const problems: DraftProblem[] = [];
  if (!parseDose(draft.dose).ok) problems.push('dose');
  if (!draft.option.routes.some((r) => r.code === draft.routeCode)) problems.push('route');
  const frequency = frequencies.find((f) => f.code === draft.frequencyCode);
  if (frequency === undefined) problems.push('frequency');
  if (
    frequency?.kind === 'as_needed' &&
    (!WHOLE.test(draft.maxDosesPerDay) || Number(draft.maxDosesPerDay) < 1)
  ) {
    problems.push('max_doses');
  }
  if (
    frequency !== undefined &&
    frequency.kind !== 'once' &&
    (!WHOLE.test(draft.durationValue) || Number(draft.durationValue) < 1)
  ) {
    problems.push('duration');
  }
  if (
    draft.quantity.trim() !== '' &&
    (!WHOLE.test(draft.quantity.trim()) || Number(draft.quantity) < 1)
  ) {
    problems.push('quantity');
  }
  return problems;
}

/** The line as the API takes it. Call only when `draftProblems` is empty. */
export function toApiLine(
  draft: DraftState,
  frequencies: readonly FrequencyView[],
): DraftLineInput {
  const frequency = frequencies.find((f) => f.code === draft.frequencyCode);
  return {
    lineKey: draft.key,
    formularyItemId: draft.option.formularyItemId,
    dose: { value: draft.dose.trim(), unit: draft.doseUnit },
    routeCode: draft.routeCode,
    frequencyCode: draft.frequencyCode,
    maxDosesPerDay: frequency?.kind === 'as_needed' ? Number(draft.maxDosesPerDay) : null,
    duration:
      frequency?.kind === 'once'
        ? null
        : { value: Number(draft.durationValue), unit: draft.durationUnit },
    quantity: draft.quantity.trim() === '' ? null : Number(draft.quantity),
    instructions: draft.instructions.trim() === '' ? null : draft.instructions.trim(),
    substitutionAllowed: draft.substitutionAllowed,
    giveInClinic: draft.giveInClinic,
  };
}

export function drugDisplay(option: FormularyOptionView): string {
  return `${option.genericName} ${option.strengthDisplay} ${option.doseForm.display.toLowerCase()}`;
}

/** "15 capsules", "2 bottle", or "15" when the base unit is unknown. */
export function quantityWords(quantity: number, unit: string | null | undefined): string {
  if (unit === null || unit === undefined || unit === '') return String(quantity);
  return `${String(quantity)} ${unit}`;
}
